// @flow
import type { Saga } from "redux-saga";
import { call, put, takeLatest, takeEvery, select } from "redux-saga/effects";
import { toast } from "react-toastify";

import { getPurchaseOrders, getUserRut } from "../selectors";
import { PurchaseOrderApi } from "../../api";
import purchaseOrderActions from "./actions";
import {
  purchaseOrderTypes,
  getListTypes,
  getByIdTypes,
  deliveryVoucherTypes,
  invoiceFileTypes,
  associatedInvoiceTypes,
  requestPaymentTypes,
  requestAutomatedPaymentTypes,
  sortPurchaseOrders,
} from "./types";
import invoiceActions from "../invoice/actions";
import { paymentStates, sortList } from "../../helpers";

export function* getPurchaseOrdersRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const {
      rut,
      providerCode = null,
      dateFrom,
      dateTo,
      orderId,
      type,
      sinisterNumber,
      state,
      isFromAdminPage = false,
    } = payload;

    const data = yield call(
      isFromAdminPage
        ? PurchaseOrderApi.getAdminPurchaseOrderList
        : PurchaseOrderApi.getPurchaseOrderList,
      {
        rut,
        providerCode,
        dateFrom,
        dateTo,
        orderId,
        type,
        sinisterNumber,
        state,
      }
    );

    if (data.success) {
      const { purchaseOrders } = data;
      yield put(
        purchaseOrderActions.getPurchaseOrdersSuccess({ purchaseOrders })
      );
    } else {
      yield put(purchaseOrderActions.getPurchaseOrdersFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getPurchaseOrdersFailed(
        "Ocurrió un error al intentar obtener las ordenes de compra."
      )
    );
  }
}

export function* getPurchaseOrderByIdRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType } = payload;
    const data = yield call(
      PurchaseOrderApi.getPurchaseOrderById,
      purchaseOrderId,
      orderType
    );

    if (data.success) {
      const { purchaseOrder } = data;
      yield put(
        purchaseOrderActions.getPurchaseOrderByIdSuccess(purchaseOrder)
      );
    } else {
      yield put(purchaseOrderActions.getPurchaseOrderByIdFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getPurchaseOrderByIdFailed(
        "Ocurrió un error al intentar obtener la ordenes de compra por Id."
      )
    );
  }
}

export function* getDeliveryVouchersRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType } = payload;
    const data = yield call(
      PurchaseOrderApi.getDeliveryVouchersByPurchaseOrder,
      purchaseOrderId,
      orderType
    );

    if (data.success) {
      const { deliveryVouchers } = data;
      yield put(
        purchaseOrderActions.getDeliveryVouchersSuccess(deliveryVouchers)
      );
    } else {
      yield put(purchaseOrderActions.getDeliveryVouchersFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getDeliveryVouchersFailed(
        "Ocurrió un error al intentar obtener los comprobantes de entrega."
      )
    );
  }
}

export function* getAssociatedInvoicesRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType } = payload;
    const data = yield call(
      PurchaseOrderApi.getAssociatedInvoicesByPurchaseOrder,
      purchaseOrderId,
      orderType
    );
    if (data.success) {
      let { invoices } = data;
      invoices = invoices.sort(
        (inA, inB) => inB.requestedPayment - inA.requestedPayment
      );
      yield put(purchaseOrderActions.getAssociatedInvoicesSuccess(invoices));
    } else {
      yield put(purchaseOrderActions.getAssociatedInvoicesFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getAssociatedInvoicesFailed(
        "Ocurrió un error al intentar obtener las facturas asociadas."
      )
    );
  }
}

export function* getAssociatedInvoicesInfoRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, type } = payload;
    const data = yield call(
      PurchaseOrderApi.getAssociatedInvoicesInfoByPurchaseOrder,
      purchaseOrderId,
      type
    );

    if (data.success) {
      const { invoicesInfo } = data;
      yield put(
        purchaseOrderActions.getAssociatedInvoicesInfoSuccess({
          purchaseOrderId,
          invoicesInfo,
        })
      );
    } else {
      yield put(
        purchaseOrderActions.getAssociatedInvoicesInfoFailed(data.message)
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getAssociatedInvoicesInfoFailed(
        "Ocurrió un error al intentar obtener la información de las facturas asociadas."
      )
    );
  }
}

export function* addDeliveryVoucherRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType, file } = payload;

    const data = yield call(
      PurchaseOrderApi.addDeliveryVoucherToPurchaseOrder,
      {
        purchaseOrderId,
        orderType,
        deliveryVoucher: { file },
      }
    );

    if (data.success) {
      yield put(purchaseOrderActions.addDeliveryVoucherSuccess());
      yield put(
        purchaseOrderActions.getDeliveryVouchersRequest({
          purchaseOrderId,
          orderType,
        })
      );
      yield call(
        toast.success,
        "Se agregó con éxito el comprobante de entrega."
      );
    } else {
      yield put(purchaseOrderActions.addDeliveryVoucherFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.addDeliveryVoucherFailed(
        "Ocurrió un error al intentar guardar un comprobante de entrega."
      )
    );
  }
}

export function* deleteDeliveryVoucherRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { deliveryVoucherId } = payload;
    const data = yield call(
      PurchaseOrderApi.deleteDeliveryVoucherById,
      deliveryVoucherId
    );

    if (data.success) {
      yield put(
        purchaseOrderActions.deleteDeliveryVoucherSuccess(deliveryVoucherId)
      );
      yield call(
        toast.success,
        "Se borró con éxito el comprobante de entrega."
      );
    } else {
      yield put(purchaseOrderActions.deleteDeliveryVoucherFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.deleteDeliveryVoucherFailed(
        "Ocurrió un error al intentar eliminar un comprobante de entrega."
      )
    );
  }
}

export function* associateManualInvoiceRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const {
      purchaseOrderId,
      orderType,
      invoiceNumber,
      date,
      amount,
      coin,
      file,
    } = payload;

    const rut = yield select(getUserRut);

    const invoiceByNumber = yield call(
      PurchaseOrderApi.getInvoiceByInvoiceNumberAndRut,
      invoiceNumber,
      rut
    );

    if (invoiceByNumber.success) {
      const exist = invoiceByNumber.invoice !== null;

      if (!exist) {
        const data = yield call(PurchaseOrderApi.associateManualInvoice, {
          purchaseOrderId,
          orderType,
          invoice: { invoiceNumber, date, amount, coin: coin || "UYU", file },
        });

        if (data.success) {
          yield put(purchaseOrderActions.associateManualInvoiceSuccess());
          yield put(
            purchaseOrderActions.getAssociatedInvoicesRequest({
              purchaseOrderId,
              orderType,
            })
          );
          yield call(
            toast.success,
            "Se agregó correctamente la factura a la Orden de Compra."
          );
        } else {
          yield put(
            purchaseOrderActions.associateManualInvoiceFailed(data.message)
          );
          yield call(toast.error, data.message);
        }
      } else {
        yield put(
          purchaseOrderActions.associateManualInvoiceFailed(
            `Ya existe una factura con el número: ${invoiceNumber}`
          )
        );
        yield call(
          toast.error,
          `Ya existe una factura con el número: ${invoiceNumber}`
        );
      }
    } else {
      yield put(
        purchaseOrderActions.associateManualInvoiceFailed(
          invoiceByNumber.message
        )
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.associateManualInvoiceFailed(
        "Ocurrió un error al intentar guardar un factura asociada."
      )
    );
  }
}

export function* deleteAssociatedInvoiceRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { invoiceId, callback } = payload;
    const data = yield call(
      PurchaseOrderApi.deleteAssociatedInvoiceById,
      invoiceId
    );

    if (data.success) {
      yield put(purchaseOrderActions.deleteAssociatedInvoiceSuccess(invoiceId));
      yield call(
        toast.success,
        "Se borró correctamente la factura de la Orden de Compra."
      );
      if (callback) {
        yield call(callback);
      }
    } else {
      yield put(
        purchaseOrderActions.deleteAssociatedInvoiceFailed(data.message)
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.deleteAssociatedInvoiceFailed(
        "Ocurrió un error al intentar eliminar una factura asociada."
      )
    );
  }
}

export function* associateElectronicInvoicesRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType, invoices } = payload;

    const data = yield call(PurchaseOrderApi.associateElectronicInvoices, {
      purchaseOrderId,
      orderType,
      invoices,
    });
    if (data.success) {
      yield put(purchaseOrderActions.associateElectronicInvoicesSuccess());
      yield put(
        purchaseOrderActions.getAssociatedInvoicesRequest({
          purchaseOrderId,
          orderType,
        })
      );
      yield put(invoiceActions.deleteElectronicInvoicesRequest(invoices));
      yield call(
        toast.success,
        "Las facturas se asociaron con éxito a la Orden de Compra."
      );
    } else {
      yield put(
        purchaseOrderActions.associateElectronicInvoicesFailed(data.message)
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.associateElectronicInvoicesFailed(
        "Ocurrió un error al intentar guardar facturas electrónicas asociadas."
      )
    );
  }
}

export function* requestPaymentRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { invoiceIds, purchaseOrderId, orderType } = payload;
    const data = yield call(PurchaseOrderApi.requestPayment, {
      invoiceIds,
      purchaseOrderId,
      orderType,
    });

    if (data.success) {
      yield put(purchaseOrderActions.requestPaymentSuccess(payload));
      yield put(
        purchaseOrderActions.getAssociatedInvoicesRequest({
          purchaseOrderId,
          orderType,
        })
      );
      yield call(toast.success, "Pago solicitado con éxito.");
    } else {
      yield put(purchaseOrderActions.requestPaymentFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.requestPaymentFailed(
        "Ocurrió un error al intentar ingresar la solicitud de pago."
      )
    );
  }
}

export function* requestAutomatedPaymentRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const {
      idPeritation,
      policyNum,
      sinisterNum,
      sinisterDate,
      purchaseOrderId,
      orderType,
      providerCode,
      amount,
      date,
      invoices,
      file,
      rut,
      providerName,
      userCode,
      coin,
      formNumber
    } = payload;
    const data = yield call(
      PurchaseOrderApi.requestAutomatedPayment,
      idPeritation,
      policyNum,
      sinisterNum,
      sinisterDate,
      purchaseOrderId,
      orderType,
      providerCode,
      amount,
      date,
      invoices,
      file,
      rut,
      providerName,
      userCode,
      coin,
      formNumber
    );

    if (data.success) {
      yield put(purchaseOrderActions.requestAutomatedPaymentSuccess(payload));
      yield call(toast.success, "Pago solicitado con éxito.");
    } else {
      yield put(
        purchaseOrderActions.requestAutomatedPaymentFailed(data.message)
      );
      yield call(toast.error, data.message);
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.requestAutomatedPaymentFailed(
        "Ocurrió un error al intentar ingresar la solicitud de pago."
      )
    );
  }
}

export function* changeInvoicesStateRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const {
      purchaseOrderId,
      orderType,
      invoices,
      newState,
      comment,
      callback,
    } = payload;
    const data = yield call(PurchaseOrderApi.changeInvoicesState, {
      invoices,
      newState,
      comment,
    });

    const { success, paymentsSuccess, errors } = data;
    if (success) {
      yield put(
        purchaseOrderActions.changeInvoicesStateSomeInvoicesAreFailed({
          paymentsSuccess,
          errors,
        })
      );

      if (purchaseOrderId) {
        yield put(
          purchaseOrderActions.getAssociatedInvoicesRequest({
            purchaseOrderId,
            orderType,
          })
        );
      } else if (callback) {
        yield call(callback);
      }
    } else {
      yield put(purchaseOrderActions.changeInvoicesStateFailed(data.message));
      yield call(toast.error, "No se pudo completar con éxito la operación.");
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.changeInvoicesStateFailed(
        "Ocurrió un error al intentar cambiar el estado de una o mas facturas solicitadas."
      )
    );
    yield call(toast.error, "No se pudo completar con éxito la operación.");
  }
}

export function* addPurchaseOrdersRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  const {
    idPeritation,
    policyNum,
    sinisterNum,
    sinisterDate,
    purchaseOrderId,
    orderType,
    providerCode,
    amount,
    date,
  } = payload;
  try {
    const data = yield call(
      PurchaseOrderApi.addPurchaseOrders,
      idPeritation,
      policyNum,
      sinisterNum,
      sinisterDate,
      purchaseOrderId,
      orderType,
      providerCode,
      amount,
      date
    );

    if (data.success) {
      yield put(
        purchaseOrderActions.addPurchaseOrdersSuccess({
          purchaseOrderId,
          orderType,
        })
      );
      yield call(toast.success, "Se agregó con éxito la orden de compra.");
    } else {
      yield put(
        purchaseOrderActions.addPurchaseOrdersFailed({
          purchaseOrderId,
          orderType,
          message: data.message,
        })
      );
      yield call(toast.error, "No se pudo completar con éxito la operación.");
    }
  } catch (error) {
    yield call(toast.error, "No se pudo completar con éxito la operación.");
    yield put(
      purchaseOrderActions.addPurchaseOrdersFailed({
        purchaseOrderId,
        orderType,
        message: "Ocurrió un error al intentar agregar una orden de compra.",
      })
    );
  }
}

export function* deletePurchaseOrderRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  const { purchaseOrderId, type } = payload;
  try {
    const data = yield call(
      PurchaseOrderApi.addPurchaseOrderDeleted,
      purchaseOrderId,
      type
    );

    if (data.success) {
      yield put(
        purchaseOrderActions.deletePurchaseOrderSuccess({ purchaseOrderId })
      );
      yield call(toast.success, "Se eliminó con éxito la orden de compra.");
    } else {
      yield put(
        purchaseOrderActions.deletePurchaseOrderFailed({
          purchaseOrderId,
          message: data.message,
        })
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.deletePurchaseOrderFailed({
        purchaseOrderId,
        message: "Ocurrió un error al intentar eliminar una orden de compra.",
      })
    );
  }
}

export function* restorePurchaseOrderRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  const { purchaseOrderId, type } = payload;
  try {
    const data = yield call(
      PurchaseOrderApi.deletePurchaseOrderDeleted,
      purchaseOrderId,
      type
    );

    if (data.success) {
      yield put(
        purchaseOrderActions.restorePurchaseOrderSuccess({ purchaseOrderId })
      );
      yield call(toast.success, "Se restauró con éxito la orden de compra.");
    } else {
      yield put(
        purchaseOrderActions.restorePurchaseOrderFailed({
          purchaseOrderId,
          message: data.message,
        })
      );
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.restorePurchaseOrderFailed({
        purchaseOrderId,
        message: "Ocurrió un error al intentar restaurar una orden de compra.",
      })
    );
  }
}

export function* sortPurchaseOrdersSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  const list = yield select(getPurchaseOrders);
  const { type, key, order } = payload;
  yield put(
    purchaseOrderActions.getPurchaseOrdersSuccess({
      purchaseOrders: sortList(type, key, order, list),
    })
  );
}

export function* getInvoiceFilesRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType } = payload;
    const data = yield call(
      PurchaseOrderApi.getInvoiceFilesByPurchaseOrder,
      purchaseOrderId,
      orderType
    );

    if (data.success) {
      const { invoiceFiles } = data;
      yield put(purchaseOrderActions.getInvoiceFilesSuccess(invoiceFiles));
    } else {
      yield put(purchaseOrderActions.getInvoiceFilesFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.getInvoiceFilesFailed(
        "Ocurrió un error al intentar obtener las facturas adjuntas."
      )
    );
  }
}

export function* addInvoiceFileRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { purchaseOrderId, orderType, file } = payload;

    const data = yield call(PurchaseOrderApi.addInvoiceFileToPurchaseOrder, {
      purchaseOrderId,
      orderType,
      invoiceFile: { file },
    });

    if (data.success) {
      yield put(purchaseOrderActions.addInvoiceFileSuccess());
      yield put(
        purchaseOrderActions.getInvoiceFilesRequest({
          purchaseOrderId,
          orderType,
        })
      );
      yield call(toast.success, "Se agregó con éxito la factura adjunta.");
    } else {
      yield put(purchaseOrderActions.addInvoiceFileFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.addInvoiceFileFailed(
        "Ocurrió un error al intentar guardar la factura adjunta."
      )
    );
  }
}

export function* deleteInvoiceFileRequestSaga({
  payload,
}: {
  payload: Object,
}): Saga {
  try {
    const { invoiceFileId } = payload;
    const data = yield call(
      PurchaseOrderApi.deleteInvoiceFileById,
      invoiceFileId
    );

    if (data.success) {
      yield put(purchaseOrderActions.deleteInvoiceFileSuccess(invoiceFileId));
      yield call(toast.success, "Se borró con éxito la factura adjunta.");
    } else {
      yield put(purchaseOrderActions.deleteInvoiceFileFailed(data.message));
    }
  } catch (error) {
    yield put(
      purchaseOrderActions.deleteInvoiceFileFailed(
        "Ocurrió un error al intentar eliminar la factura adjunta."
      )
    );
  }
}

export default function* purchaseOrderSaga(): Saga {
  yield takeLatest(getListTypes.REQUEST, getPurchaseOrdersRequestSaga);
  yield takeLatest(getByIdTypes.REQUEST, getPurchaseOrderByIdRequestSaga);
  yield takeLatest(
    deliveryVoucherTypes.GET_REQUEST,
    getDeliveryVouchersRequestSaga
  );
  yield takeLatest(
    associatedInvoiceTypes.GET_REQUEST,
    getAssociatedInvoicesRequestSaga
  );
  yield takeEvery(
    associatedInvoiceTypes.GET_INFO_REQUEST,
    getAssociatedInvoicesInfoRequestSaga
  );
  yield takeLatest(
    deliveryVoucherTypes.ADD_REQUEST,
    addDeliveryVoucherRequestSaga
  );
  yield takeLatest(
    deliveryVoucherTypes.DELETE_REQUEST,
    deleteDeliveryVoucherRequestSaga
  );
  yield takeLatest(
    associatedInvoiceTypes.ADD_REQUEST,
    associateManualInvoiceRequestSaga
  );
  yield takeLatest(
    associatedInvoiceTypes.DELETE_REQUEST,
    deleteAssociatedInvoiceRequestSaga
  );
  yield takeLatest(
    associatedInvoiceTypes.ADD_E_REQUEST,
    associateElectronicInvoicesRequestSaga
  );
  yield takeLatest(requestPaymentTypes.REQUEST, requestPaymentRequestSaga);
  yield takeLatest(
    requestAutomatedPaymentTypes.REQUEST,
    requestAutomatedPaymentRequestSaga
  );
  yield takeLatest(
    requestPaymentTypes.CHANGE_STATE_REQUEST,
    changeInvoicesStateRequestSaga
  );

  yield takeLatest(
    purchaseOrderTypes.ADD_REQUEST,
    addPurchaseOrdersRequestSaga
  );
  yield takeLatest(
    purchaseOrderTypes.DELETE_REQUEST,
    deletePurchaseOrderRequestSaga
  );
  yield takeLatest(
    purchaseOrderTypes.RESTORE_REQUEST,
    restorePurchaseOrderRequestSaga
  );
  yield takeLatest(sortPurchaseOrders.SORT, sortPurchaseOrdersSaga);

  yield takeLatest(invoiceFileTypes.GET_REQUEST, getInvoiceFilesRequestSaga);
  yield takeLatest(invoiceFileTypes.ADD_REQUEST, addInvoiceFileRequestSaga);
  yield takeLatest(
    invoiceFileTypes.DELETE_REQUEST,
    deleteInvoiceFileRequestSaga
  );
}
